import { KitContainedIcon, KitLink, KitText } from '@omni/kit/components';
import { KitVerifyCodeInput } from '@omni/kit/components';
import { KitButton } from '@omni/kit/components';
import KitSnack, {
  KitSnackDuration,
  KitSnackRender,
} from '@omni/kit/components/KitSnack';
import Show from '@omni/kit/components/Show';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import sanitizePhoneNumber from '@omni/kit/utilities/sanitizePhoneNumber';
import React, { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import GetAppHeaders from '../Downloader/GetAppHeaders';
import { IS_LOCALHOST } from '../Environment';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '../constants/identifiers';
import useShellAuthProviders from '../contexts/ShellContext/hooks/useShellAuthProviders';
import { useCountdownTimer } from '../hooks/useCountdownTimer';
import { handleErrorResponse } from '../utilities/utilities';
import NoAccountFoundScreen from './NoAccountFoundScreen';
import PhoneLoginDisabledScreen from './PhoneLoginDisabledScreen';
import { AuthService } from './Service';
import { AuthProvider } from './Service/Types';

interface VerifyCodeScreenProps {
  authUrl: string;
  confirmUrl: string;
  phoneAuthState: string;
  phoneNumber: string;
  callingCode: string;
  regionCode: string;
  setPhoneNumber: (phoneNumber: string) => void;
  setVerifyCodeScreenVisible: (visible: boolean) => void;
  customReturnUrl?: string;
  source?: string;
  targetProvider?: AuthProvider;
}

const VerifyCodeScreen = ({
  authUrl,
  confirmUrl,
  phoneAuthState,
  phoneNumber,
  callingCode,
  regionCode,
  setPhoneNumber,
  setVerifyCodeScreenVisible,
  customReturnUrl,
  source,
  targetProvider,
}: VerifyCodeScreenProps): JSX.Element => {
  // ------------------------------------------------------------
  // Context & Custom Hooks

  const {
    counter,
    isActive: isResendCodeDisabled,
    start: startCountdown,
    stop: stopCountdown,
  } = useCountdownTimer();
  const { loginEnabledProviders } = useShellAuthProviders();
  const { app, tokens, dispatchEvent } = useShellContext();
  const { sizeClassV2 } = useSizeClass();
  const { t } = useTranslation(['auth']);

  // ------------------------------------------------------------
  // State

  const [verificationCode, setVerificationCode] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
    marginBottom: Spacing.xxl,
  });
  const [noAccountFoundScreenVisible, setNoAccountFoundScreenVisible] =
    useState(false);
  const [phoneLoginDisabledScreenVisible, setPhoneLoginDisabledScreenVisible] =
    useState(false);

  // ------------------------------------------------------------
  // Computed Values

  const isMobileDevice = sizeClassV2 < SizeClassV2.M;

  // ------------------------------------------------------------
  // Event Handlers & Callbacks

  const confirmVerificationCode = async (): Promise<void> => {
    const appHeaders = await GetAppHeaders();

    setIsVerifying(true);

    if (confirmUrl) {
      const formData = new URLSearchParams({
        code: verificationCode,
        grant_type: 'otp',
        phone: `${callingCode}${sanitizePhoneNumber(phoneNumber)}`,
        state: phoneAuthState,
      });

      const response = await fetch(confirmUrl, {
        method: 'POST',
        body: formData.toString(),
        // credentials: 'include' is required in order for the backend EUA
        // result handler to set the sap_token cookie
        ...(Platform.OS === 'web' &&
          !IS_LOCALHOST && { credentials: 'include' }),
        headers: {
          ...appHeaders,
          Authorization: `Bearer ${tokens.guest}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        if (response.url) {
          const resultUrl = response.url;
          const urlParams = new URL(resultUrl).searchParams;
          const accessToken = urlParams.get('access_token');
          const sapToken = urlParams.get('sap_token');
          const subsplashProvider = loginEnabledProviders.find(
            (provider) => provider.authproviderid === SUBSPLASH_AUTH_PROVIDER_ID
          ) as AuthProvider;

          if (accessToken && sapToken) {
            AuthService.setAccessToken(SUBSPLASH_AUTH_PROVIDER_ID, accessToken);
            AuthService.refreshToken = sapToken;
            AuthService.setRefreshTokenEndpoint(
              SUBSPLASH_AUTH_PROVIDER_ID,
              subsplashProvider.token_url
            );
            AuthService.sendTokenDataToNative({
              authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
              accessToken,
              refreshToken: sapToken,
            });

            await AuthService.loadCredentials({
              appKey: app.appKey,
              targetProviders: [subsplashProvider],
              sapToken,
            });

            AuthService.notifyAuthorizationChanged();

            if (Platform.OS === 'web') {
              // update login state in the web-app
              dispatchEvent({
                type: 'AUTH_CALLBACK',
                payload: {
                  refresh: sapToken,
                },
              });
            }
          }
        }
      } else {
        const responseData = await response.json();

        if (
          response.status === 401 &&
          responseData.error.includes('invalid code')
        ) {
          setErrorMessage(
            t('auth:invalidCode', {
              remainingAttempts: responseData.remaining_attempts,
              attempts:
                responseData.remaining_attempts === 1
                  ? t('auth:remainingAttempt')
                  : t('auth:remainingAttempts'),
            })
          );
          setShowError(true);
        }

        if (
          response.status === 423 &&
          (responseData.error.includes('too many failed attempts') ||
            responseData.error.includes(
              'account locked due to too many failed attempts'
            ))
        ) {
          setPhoneLoginDisabledScreenVisible(true);
        }

        if (
          response.status === 404 &&
          responseData.error.includes('authenticator not found')
        ) {
          setNoAccountFoundScreenVisible(true);
        }
      }
    }

    setIsVerifying(false);
  };

  const resendCode = async (): Promise<void> => {
    setVerificationCode('');

    const appHeaders = await GetAppHeaders();

    const response = await fetch(authUrl, {
      method: 'POST',
      body: JSON.stringify({
        phone_number: {
          significant: sanitizePhoneNumber(phoneNumber),
          country: {
            region_code: regionCode,
            calling_code: callingCode,
          },
        },
        ...(Platform.OS === 'web' && {
          skip_deep_link: true,
          use_refresh_cookie: !IS_LOCALHOST,
        }),
      }),
      headers: {
        ...appHeaders,
        Authorization: `Bearer ${tokens.guest}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      startCountdown();
    } else {
      const { message } = await handleErrorResponse(response);

      if (isMobileDevice) {
        showKitSnack(
          message ?? t('auth:newCodeSendFailed'),
          KitSnackDuration.SHORT
        );
      } else {
        KitSnack.show(message ?? t('auth:newCodeSendFailed'));
      }
    }
  };

  const showKitSnack = (message: string, duration: number): void => {
    setSnackOptions({
      duration,
      marginBottom: Spacing.xxl,
      message,
      visible: true,
    });
  };

  const reset = () => {
    stopCountdown();
    setVerifyCodeScreenVisible(false);
    setPhoneNumber('');
  };

  // ------------------------------------------------------------
  // Render Helpers

  if (phoneLoginDisabledScreenVisible) {
    return <PhoneLoginDisabledScreen reset={reset} />;
  }

  if (noAccountFoundScreenVisible) {
    return (
      <NoAccountFoundScreen
        phoneNumber={phoneNumber}
        appKey={app.appKey}
        customReturnUrl={customReturnUrl}
        loginEnabledProviders={loginEnabledProviders}
        openInNewTab={false}
        source={source}
        targetProvider={targetProvider}
        reset={reset}
      />
    );
  }

  return (
    <View
      style={[styles.container, !isMobileDevice && { marginHorizontal: 100 }]}
    >
      <View style={styles.iconContainer}>
        <KitContainedIcon name='message' size='md' />
      </View>
      <View>
        <KitText center h1 style={styles.title} color={Colors.N900}>
          {t('auth:verifyCodePromptTitle')}
        </KitText>
        <KitText center style={styles.subtitle} color={Colors.N900}>
          {t('auth:verifyCodePromptSubtitle', {
            phoneNumber: `${callingCode} ${phoneNumber}`,
          })}
        </KitText>
      </View>
      <KitVerifyCodeInput
        setValue={(code) => {
          setVerificationCode(code);
          setShowError(false);
        }}
        value={verificationCode}
        returnKeyType='go'
        blurOnSubmit
        numeric
        onSubmitEditing={() => {
          if (!isVerifying && verificationCode.length === 6) {
            confirmVerificationCode();
          }
        }}
      >
        <View
          style={{
            gap: Spacing.m,
            marginTop: Spacing.xl,
          }}
        >
          <Show show={showError}>
            <View style={{ ...styles.errorContainer, marginBottom: 6 }}>
              <KitText color={Colors.N0} fontSize={16}>
                {errorMessage}
              </KitText>
            </View>
          </Show>
          <KitButton
            color={Colors.N1000}
            disabled={isVerifying || verificationCode.length !== 6}
            onPress={confirmVerificationCode}
            isLoading={isVerifying}
            title={t('auth:verifyCode')}
          />
          <KitButton
            secondary
            onPress={resendCode}
            title={
              isResendCodeDisabled
                ? t('auth:requestNewCodeIn', {
                    seconds: counter,
                  })
                : t('auth:iDidntGetCode')
            }
            disabled={isResendCodeDisabled || isVerifying}
          />
        </View>
      </KitVerifyCodeInput>
      <KitLink fontSize={16} style={styles.link} onPress={reset}>
        {t('auth:logInAnotherWay')}
      </KitLink>
      <KitSnackRender
        {...snackOptions}
        setVisible={(value) =>
          setSnackOptions({ ...snackOptions, visible: value })
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    justifyContent: 'center',
    paddingHorizontal: Spacing.xl,
    marginBottom: Spacing.xxxxxl,
  },
  errorContainer: {
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.m,
    backgroundColor: Colors.R500,
    borderRadius: BorderRadius.m,
    marginBottom: Spacing.l,
    width: '100%',
  },
  iconContainer: {
    marginBottom: Spacing.l,
    alignItems: 'center',
  },
  link: {
    color: Colors.N900,
    marginTop: Spacing.xl,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: Spacing.xl,
  },
  text: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333333',
  },
  title: {
    textAlign: 'center',
    marginBottom: Spacing.s,
  },
});

export default VerifyCodeScreen;
